import { useTranslation } from "react-i18next";
import type { InvoiceKind, RegistryStatusFilter } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface InvoiceFilterState {
  status: RegistryStatusFilter | "all";
  instance: string;
  from: string;
  to: string;
  kind: InvoiceKind | "all";
  oobOnly: boolean;
}

export const EMPTY_FILTERS: InvoiceFilterState = {
  status: "all",
  instance: "",
  from: "",
  to: "",
  kind: "all",
  oobOnly: false,
};

const STATUSES: RegistryStatusFilter[] = [
  "open",
  "overdue",
  "paid",
  "partially_refunded",
  "refunded",
  "void",
  "uncollectible",
];
const KINDS: InvoiceKind[] = ["subscription", "one_off"];

/** Same shape the API accepts for an instance name; anything else is not sent. */
const INSTANCE_NAME = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/** Filter state -> query string for /invoices and /invoices.csv. */
export function filtersToParams(f: InvoiceFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (f.status !== "all") params.set("status", f.status);
  const instance = f.instance.trim();
  if (INSTANCE_NAME.test(instance)) params.set("instance", instance);
  if (f.from) params.set("from", f.from);
  if (f.to) params.set("to", f.to);
  if (f.kind !== "all") params.set("kind", f.kind);
  if (f.oobOnly) params.set("oob", "true");
  return params;
}

export function InvoiceFilters({
  value,
  onChange,
}: {
  value: InvoiceFilterState;
  onChange: (next: InvoiceFilterState) => void;
}) {
  const { t } = useTranslation("billing");
  const set = <K extends keyof InvoiceFilterState>(key: K, v: InvoiceFilterState[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <Card>
      <CardContent className="flex flex-wrap items-end gap-4 pt-6">
        <div className="space-y-1">
          <Label htmlFor="inv-status">{t("payments.filters.status")}</Label>
          <Select
            value={value.status}
            onValueChange={(v) => set("status", v as InvoiceFilterState["status"])}
          >
            <SelectTrigger id="inv-status" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("payments.filters.all")}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`payments.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-instance">{t("payments.filters.instance")}</Label>
          <Input
            id="inv-instance"
            className="h-9 w-48"
            placeholder={t("payments.filters.instancePlaceholder")}
            value={value.instance}
            onChange={(e) => set("instance", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-from">{t("payments.filters.from")}</Label>
          <Input
            id="inv-from"
            type="date"
            className="h-9 w-40"
            value={value.from}
            max={value.to || undefined}
            onChange={(e) => set("from", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-to">{t("payments.filters.to")}</Label>
          <Input
            id="inv-to"
            type="date"
            className="h-9 w-40"
            value={value.to}
            min={value.from || undefined}
            onChange={(e) => set("to", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-kind">{t("payments.filters.kind")}</Label>
          <Select
            value={value.kind}
            onValueChange={(v) => set("kind", v as InvoiceFilterState["kind"])}
          >
            <SelectTrigger id="inv-kind" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("payments.filters.all")}</SelectItem>
              {KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {t(`payments.kind.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex h-9 items-center gap-2">
          <Switch
            id="inv-oob"
            checked={value.oobOnly}
            onCheckedChange={(v) => set("oobOnly", v)}
          />
          <Label htmlFor="inv-oob" className="cursor-pointer">
            {t("payments.filters.oob")}
          </Label>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTERS)}>
          {t("payments.filters.reset")}
        </Button>
      </CardContent>
    </Card>
  );
}
